<template>
  <div>
    <h4>{{ trans('Account') }}</h4>

    <!-- Email -->
    <MdlTextfield
      v-if="isInvited"
      ref="email"
      name="email"
      disabled
      :floating-label="trans('E-mail')"
      :model-value="registration.email"
    />
    <MdlTextfield
      v-else
      ref="email"
      name="email"
      required
      class="block"
      autocomplete="new-password"
      :readonly="isInvited"
      :floating-label="trans('E-mail')"
      :error="emailError"
      error-focus
      v-model="registration.email"
      @blur="checkEmailAvailable"
    />

    <!-- Repeat email -->
    <input v-if="isInvited" type="hidden" name="email_confirm" :value="registration.emailRepeat" />
    <MdlTextfield
      v-else
      name="email_confirm"
      class="block"
      autocomplete="new-password"
      :floating-label="trans('Repeat e-mail')"
      :error="repeatEmailError"
      error-focus
      v-model="registration.emailRepeat"
      @blur="validateRepeatEmail"
    />

    <!-- Password -->
    <MdlTextfieldPassword
      ref="password"
      name="password"
      required
      class="block"
      autocomplete="new-password"
      :floating-label="trans('Password')"
      :error="passwordError"
      error-focus
      v-model="registration.password"
      v-model:error="passwordError"
    />

    <h4 class="mt-8">{{ trans('Profile') }}</h4>

    <div class="flex items-center gap-x-2 w-full mb-8">
      <!-- Firstname -->
      <MdlTextfield
        name="firstname"
        required
        class="flex-grow"
        autocomplete="new-password"
        ref="firstname"
        :error="firstnameError"
        error-focus
        :floating-label="trans('Firstname')"
        v-model="registration.firstname"
      />

      <!-- Lastname -->
      <MdlTextfield
        name="lastname"
        required
        class="flex-grow"
        autocomplete="new-password"
        ref="lastname"
        :error="lastnameError"
        error-focus
        :floating-label="trans('Lastname')"
        v-model="registration.lastname"
      />
    </div>
  </div>
</template>

<script>
import { trans } from '@/munio/i18n'
import MdlTextfield from '@component/mdl/Textfield.vue'
import MdlTextfieldPassword from '@component/mdl/TextfieldPassword.vue'

export default {
  inject: ['registration'],

  components: {
    MdlTextfield,
    MdlTextfieldPassword,
  },

  data() {
    return {
      firstnameError: null,
      lastnameError: null,
      emailError: null,
      passwordError: null,
      repeatEmailError: null,
      isAvailableEmail: false,
    }
  },

  computed: {
    isInvited() {
      return this.$store.state.userInvited || this.$store.state.user?.isInvited
    },

    userInvite() {
      if (this.$store.state.userInvite) {
        return this.$store.state.userInvite.uuid
      }

      return null
    },
  },

  methods: {
    trans,

    validateName() {
      this.firstnameError = !this.registration.firstname ? trans('Please enter your firstname') : null
      this.lastnameError = !this.registration.lastname ? trans('Please enter your lastname') : null
    },

    validateEmail() {
      const { email } = this.registration
      this.isAvailableEmail = false
      this.emailError = null

      if (!email) {
        this.emailError = trans('validation.custom.email.required')
      } else if (!this.validEmail(email)) {
        this.emailError = trans('validation.custom.email.email')
      }
    },

    validateRepeatEmail() {
      const { email, emailRepeat } = this.registration

      this.repeatEmailError = email !== emailRepeat ? trans('validation.custom.email.confirmed') : null
    },

    async checkEmailAvailable() {
      this.validateEmail()

      if (this.emailError) {
        return false
      }

      const { email } = this.registration

      this.$store.commit('loading', true)

      let user = null

      try {
        let response = await Munio.api.auth.checkEmailAvailable(email)

        if (response.status === 200) {
          if (response.data.ecoIdRedirect) {
            this.$store.commit('updateEcoIdRedirect', response.data.ecoIdRedirect)
            return
          }

          if (response.data.data && response.data.meta) {
            this.$store.commit('updateEmployerList', {
              employerList: response.data.data,
              rootCompanyId: response.data.meta.companyId,
            })

            this.registration.company =
              response.data.data.find((company) => company.id === this.registration.company?.id) || null

            this.isAvailableEmail = true
            return
          }

          if (response.data.data) {
            user = response.data.data
            let validate = false
            if (!this.registration.firstname) {
              this.registration.firstname = user?.firstname
              validate = true
            }
            if (!this.registration.lastname) {
              this.registration.lastname = user?.lastname
              validate = true
            }
            if (validate) {
              this.validateName()
            }
          }
        } else {
          this.$store.commit('updateEmployerList', { employerList: [], rootCompanyId: null })
        }

        this.isAvailableEmail = true
      } catch (err) {
        if (err.response && err.response.status === 422) {
          this.emailError =
            err.response.data.error?.message?.email?.[0] ||
            err.response.data.error?.message?.email ||
            err.response.data.errors?.email?.[0] ||
            err.response.data.message
        } else {
          this.emailError = trans('This e-mail is already registered. Please log in instead.')
        }
        this.isAvailableEmail = false
      } finally {
        this.$store.commit('loading', false)
      }
    },

    validEmail(email) {
      return /@/.test(email) // dont do email validation in javascript
    },

    async validate() {
      this.validateName()
      this.validateEmail()
      this.validateRepeatEmail()

      await this.checkEmailAvailable()

      return (
        this.isAvailableEmail &&
        !this.firstnameError &&
        !this.lastnameError &&
        !this.emailError &&
        !this.repeatEmailError &&
        !this.passwordError
      )
    },
  },

  mounted() {
    if (this.isInvited) {
      this.$refs.password.focus()
    } else {
      this.$refs.email.focus()
    }
  },
}
</script>
